import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Badge, Button } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { planNameMessages } from '../../music24/config/music24PlanNames';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { teamPlanLabel } from '../../team/teamAcceptMessages';
import { useTeamListQuery } from '../../team/useTeamListQuery';
import { useWorkspaces } from '../../workspaces/useWorkspaces';
import { WorkspaceMark } from '../../workspaces/WorkspaceMark';
import { SubscriptionMemberNotice } from './SubscriptionMemberNotice';
import { SubscriptionMemberLimits } from './SubscriptionMemberLimits';
import { SubscriptionMemberOwnerNote } from './SubscriptionMemberOwnerNote';
import { subscriptionMemberMessages as messages } from './subscriptionMemberMessages';
import styles from './SubscriptionMemberPlan.module.scss';

const dateFormat = { year: 'numeric', month: 'short', day: 'numeric' } as const;

/**
 * design: workspaces f1 / f2. A member of the active workspace sees its plan, "Managed by {owner}",
 * the limits and whom to ask, with no checkout, upgrade, portal or cancel button. The plan and
 * the limits are the active workspace's (the licence in the token). A plan that lapsed to Free
 * (f2) shows the owner note only. Without `workspaces.list`, the o6 notice stays.
 */
const SubscriptionMemberPlanComponent: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { active, isLoading } = useWorkspaces();
  const capabilities = useMusic24Capabilities();
  const licenseInfo = useLicenseInfo();
  const team = useTeamListQuery();
  const openPlans = useCallback(() => {
    dispatch(toggleSMLPricingModal({ shouldShow: true }));
  }, [dispatch]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (active === null) {
    return <SubscriptionMemberNotice />;
  }
  const isLapsed = capabilities.plan === 'free';
  const planName = intl.formatMessage(planNameMessages[capabilities.plan]);
  const planLabel = teamPlanLabel(intl, capabilities.plan);
  const renewsAt = licenseInfo.subscriptionExpiresAt;
  const endedAt = licenseInfo.license?.subscriptionExpiresAt ?? null;
  const seatsUsed = team.data === undefined ? undefined : team.data.members.length + team.data.pendingInvites.length;

  const subtitle = !isLapsed
    ? intl.formatMessage(messages.intro)
    : endedAt === null
      ? intl.formatMessage(messages.ended)
      : intl.formatMessage(messages.endedOn, { date: intl.formatDate(endedAt, dateFormat) });
  const rowDetail = isLapsed
    ? intl.formatMessage(messages.freeArtists, {
        plan: planLabel,
        count: active.artistCount,
        limit: capabilities.artistsLimit ?? 0,
      })
    : renewsAt === undefined
      ? planLabel
      : intl.formatMessage(messages.planRenews, { plan: planLabel, date: intl.formatDate(renewsAt, dateFormat) });

  return (
    <Panel as="section">
      <PanelHead
        titleAs="h2"
        title={
          isLapsed
            ? intl.formatMessage(messages.onFree, { workspace: active.name })
            : intl.formatMessage(messages.onPlan, { workspace: active.name, plan: planName })
        }
        subtitle={subtitle}
        right={
          <span className={styles.managed} data-clarity-mask="true">
            {intl.formatMessage(messages.managedBy, { email: active.ownerEmail })}
          </span>
        }
      />
      <PanelBody stack>
        <Panel nested className={styles.row}>
          <WorkspaceMark licenseId={active.licenseId} name={active.name} size="large" />
          <div className={styles.rowText}>
            <b>{active.name}</b>
            <small className={styles.muted}>{rowDetail}</small>
          </div>
          <Badge color="secondary" pill>
            {intl.formatMessage({ id: 'app.sml.team.role.member', defaultMessage: 'Member' })}
          </Badge>
        </Panel>
        {!isLapsed && (
          <SubscriptionMemberLimits
            capabilities={capabilities}
            artistCount={active.artistCount}
            seatsUsed={seatsUsed}
          />
        )}
        <SubscriptionMemberOwnerNote ownerEmail={active.ownerEmail} workspaceName={active.name} isLapsed={isLapsed} />
        {!isLapsed && (
          <div>
            <Button color="link" className="p-0" onClick={openPlans}>
              {intl.formatMessage(messages.seePlan)}
            </Button>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const SubscriptionMemberPlan = TypedMemo(SubscriptionMemberPlanComponent);
